import { useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { zodResolver } from '@hookform/resolvers/zod';

import { COUNTRIES } from '@/config';
import { useCreateOrganizationMutation, useLazyGetMeQuery } from '@/features/auth/store/api';
import { updateUser } from '@/features/auth/store/authSlice';
import { PrimaryButton, SecondaryButton, Combobox, Input, FormField } from '@/lib/ui';
import { extractServerError, extractServerFieldErrors, notify } from '@/lib/utils';
import { ROUTES } from '@/routes';
import { useAppDispatch } from '@/store/hooks';

import { useSignOut } from '../hooks';
import { OrgSetupSchema } from '../schemas';
import type { OrgSetupFormValues } from '../types';
import { AuthScreenShell } from './AuthScreenShell';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fieldCls =
  'border-white/10 bg-black/40 text-white placeholder:text-white/45 ring-offset-background';

// Server field names that map 1:1 onto inputs in this form; anything else
// (e.g. `organization` when the user already has one) goes to a toast.
const FORM_FIELDS = ['name', 'website', 'contact_mail', 'admin_email'] as const;
type ServerMappedField = (typeof FORM_FIELDS)[number];

const isFormField = (field: string): field is ServerMappedField =>
  (FORM_FIELDS as readonly string[]).includes(field);

// RHF error type for 409s from the API, so the website field can tell
// "this org already uses Moveris" apart from its own validation errors.
const SERVER_CONFLICT = 'server-conflict';
const SUPPORT_EMAIL = 'support@moveris.com';

// ── Component ─────────────────────────────────────────────────────────────────

export function OrgSetupForm() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const signOut = useSignOut();
  const [createOrganization, { isLoading }] = useCreateOrganizationMutation();
  const [triggerGetMe, { isFetching: isRefreshingUser }] = useLazyGetMeQuery();
  // Set when the org was created but the follow-up /me failed. Submitting again
  // would only hit "already belongs to an organization", so we retry /me instead.
  const [orgCreated, setOrgCreated] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<OrgSetupFormValues>({ resolver: zodResolver(OrgSetupSchema) });

  const onSubmit = async (data: OrgSetupFormValues) => {
    try {
      await createOrganization({ organization: data }).unwrap();
    } catch (err) {
      const fieldErrors = extractServerFieldErrors(err).filter(({ field }) => isFormField(field));
      fieldErrors.forEach(({ field, detail }) => {
        setError(field as ServerMappedField, {
          type: SERVER_CONFLICT,
          message: Array.isArray(detail) ? detail.join(' ') : detail,
        });
      });
      if (fieldErrors.length === 0) {
        notify.error(extractServerError(err, 'Failed to save organization. Please try again.'));
      }
      return;
    }

    notify.success('Organization created.');
    await refreshUserAndContinue();
  };

  // The guards read the stored user, so it must show the new org before we
  // navigate; otherwise ProtectedLayout bounces straight back here.
  const refreshUserAndContinue = async () => {
    try {
      const me = await triggerGetMe().unwrap();
      dispatch(updateUser(me.user));
      navigate(ROUTES.dashboard, { replace: true });
    } catch {
      setOrgCreated(true);
    }
  };

  return (
    <AuthScreenShell maxWidthClass="max-w-lg">
      <div className="space-y-1">
        <p className="text-sm font-semibold text-white">Organization details</p>
        <p className="text-xs text-white/55">
          Tell us about your organization to finish setting up your account.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <FormField
          label="Organization name"
          htmlFor="org-name"
          error={errors.name?.message}
          labelClassName="!text-white !font-semibold"
        >
          <Input id="org-name" placeholder="Acme Corp" className={fieldCls} {...register('name')} />
        </FormField>

        <div className="space-y-1.5">
          <FormField
            label="Website URL"
            htmlFor="org-website"
            error={errors.website?.message}
            labelClassName="!text-white !font-semibold"
          >
            <Input
              id="org-website"
              type="url"
              placeholder="https://example.com"
              className={fieldCls}
              {...register('website')}
            />
          </FormField>
          {/* The org already exists; only staff can add this user to it for now. */}
          {errors.website?.type === SERVER_CONFLICT && (
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-xs font-medium text-white underline hover:no-underline"
            >
              Contact support
            </a>
          )}
        </div>

        <FormField
          label="Address"
          htmlFor="org-address"
          error={errors.address?.message}
          labelClassName="!text-white !font-semibold"
        >
          <textarea
            id="org-address"
            placeholder="123 Main St, City, State 12345"
            rows={3}
            className="flex w-full rounded-md border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 border-white/10 bg-black/40 text-white placeholder:text-white/45 ring-offset-background resize-none"
            {...register('address')}
          />
        </FormField>

        <FormField
          label="Country"
          htmlFor="org-country"
          error={errors.country?.message}
          labelClassName="!text-white !font-semibold"
        >
          <Controller
            control={control}
            name="country"
            render={({ field }) => (
              <Combobox
                options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
                value={field.value}
                onChange={field.onChange}
                placeholder="Select your country"
                searchPlaceholder="Search country..."
                className={fieldCls}
              />
            )}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Phone number"
            htmlFor="org-phone"
            error={errors.contact_phone_number?.message}
            labelClassName="!text-white !font-semibold"
          >
            <Input
              id="org-phone"
              type="tel"
              placeholder="+1 555 000 0000"
              className={fieldCls}
              {...register('contact_phone_number')}
            />
          </FormField>

          <FormField
            label="Organization email"
            htmlFor="org-email"
            error={errors.contact_mail?.message}
            labelClassName="!text-white !font-semibold"
          >
            <Input
              id="org-email"
              type="email"
              placeholder="contact@example.com"
              className={fieldCls}
              {...register('contact_mail')}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Admin name"
            htmlFor="org-admin-name"
            error={errors.admin_name?.message}
            labelClassName="!text-white !font-semibold"
          >
            <Input
              id="org-admin-name"
              placeholder="Jane Doe"
              className={fieldCls}
              {...register('admin_name')}
            />
          </FormField>

          <FormField
            label="Admin email"
            htmlFor="org-admin-email"
            error={errors.admin_email?.message}
            labelClassName="!text-white !font-semibold"
          >
            <Input
              id="org-admin-email"
              type="email"
              placeholder="admin@example.com"
              className={fieldCls}
              {...register('admin_email')}
            />
          </FormField>
        </div>

        {orgCreated && (
          <div role="alert" className="rounded-lg border border-warning/30 bg-warning/10 p-3">
            <p className="text-sm text-warning-dark dark:text-warning">
              Your organization was created, but we couldn&apos;t refresh your account.
            </p>
            <button
              type="button"
              onClick={refreshUserAndContinue}
              disabled={isRefreshingUser}
              className="mt-1 text-xs text-white/60 underline hover:text-white disabled:opacity-50"
            >
              {isRefreshingUser ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <SecondaryButton
            type="button"
            className="flex-1"
            disabled={isLoading}
            // OrgSetupLayout redirects to /login once the token is cleared.
            onClick={signOut}
          >
            Sign out
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            className="flex-1 font-semibold"
            disabled={isLoading || orgCreated}
          >
            {isLoading ? 'Saving…' : 'Submit'}
          </PrimaryButton>
        </div>
      </form>
    </AuthScreenShell>
  );
}
